import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { SiteScaffoldChat } from '../../types';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { QuickPromptBar } from '../QuickPromptBar';
import ChatMarkdown from '../common/ChatMarkdown';
import ContextUsageChip from '../ContextUsageChip';
import { AgentErrorBanner, AgentStatusChip } from '../common/AgentErrorBanner';
import { starterPrompts } from './siteSpec';

/**
 * The designer conversation.
 *
 * THREE INDEPENDENT REFRESH TRIGGERS, and the redundancy is deliberate — this
 * codebase already learned why (see `SiteParsers.tsx`, the `wasWorkingRef`
 * comment). Keying the "a round finished" callback off the poll alone silently
 * never fires: the websocket event's refetch flips the status to idle, the poll
 * effect re-runs, cleanup clears the interval, and the callback never observes
 * the change. So:
 *
 *   1. the thinking -> idle TRANSITION, watched here;
 *   2. the `site_scaffold_chat` "replied" event, which also carries
 *      `spec_changed` so the studio can skip a repaint it doesn't need;
 *   3. a 2s poll while thinking, as the missed-event fallback.
 *
 * All three converge on the parent's `onRoundFinished`. Preview renders are
 * pure server-side compiles, so triple-firing costs one cheap request.
 */
export function SiteScaffoldChatPane({
  siteId,
  spec,
  isPublished,
  onRoundFinished,
  pendingMessage,
  onPendingConsumed,
  onBusyChange,
}: {
  siteId: number;
  spec: any;
  isPublished: boolean;
  onRoundFinished: (specChanged: boolean) => void;
  /** A message the parent wants sent — see the "match a site" bar. */
  pendingMessage?: string | null;
  onPendingConsumed?: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [chat, setChat] = useState<SiteScaffoldChat | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [promptPage, setPromptPage] = useState(0);
  const pollRef = useRef<any>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const specChangedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await api.getSiteScaffoldChat(siteId);
      setChat(next);
      return next;
    } catch {
      return null;
    }
  }, [siteId]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const { subscribe } = useWebSocket();
  useEffect(
    () =>
      subscribe((event: any) => {
        if (event?.event_type !== 'site_scaffold_chat' || event.site_id !== siteId) return;
        // Remember what the event said before the refetch races it — the
        // transition effect below is what actually notifies the parent.
        if (event.status === 'replied') specChangedRef.current = Boolean(event.spec_changed);
        load();
      }),
    [subscribe, siteId, load],
  );

  useEffect(() => {
    clearInterval(pollRef.current);
    if (chat?.status === 'thinking') pollRef.current = setInterval(load, 2000);
    return () => clearInterval(pollRef.current);
  }, [chat?.status, load]);

  const wasThinkingRef = useRef(false);
  useEffect(() => {
    const thinking = chat?.status === 'thinking';
    if (wasThinkingRef.current && !thinking) {
      // Default to true when no event told us otherwise: a needless repaint is
      // one cheap request, a missed one leaves the user looking at stale work.
      onRoundFinished(specChangedRef.current !== false);
      specChangedRef.current = false;
    }
    wasThinkingRef.current = thinking;
  }, [chat?.status, onRoundFinished]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages.length, chat?.status]);

  const send = async (text?: string) => {
    const body = (text ?? input).trim();
    if (!body || busy) return;
    if (text === undefined) setInput('');
    setBusy(true);
    setError('');
    try {
      setChat(await api.sendSiteScaffoldChat(siteId, body));
    } catch (e: any) {
      setError(e?.message || 'Could not send that message.');
    } finally {
      setBusy(false);
    }
  };

  const designItForMe = async () => {
    setBusy(true);
    setError('');
    try {
      setChat(await api.startSiteScaffoldDesignPass(siteId));
    } catch (e: any) {
      setError(e?.message || 'Could not start a design pass.');
    } finally {
      setBusy(false);
    }
  };

  const newChat = async () => {
    setBusy(true);
    try {
      setChat(await api.clearSiteScaffoldChat(siteId));
    } catch (e: any) {
      setError(e?.message || 'Could not start a new session.');
    } finally {
      setBusy(false);
    }
  };

  const thinking = chat?.status === 'thinking';
  const messages = chat?.messages || [];

  // Let the parent disable its own controls while a round is running, rather
  // than each of them keeping a second copy of "is the agent busy".
  useEffect(() => {
    onBusyChange?.(thinking || busy);
  }, [thinking, busy, onBusyChange]);

  // Send whatever the parent handed us, once. Guarded on `thinking` so a
  // second request queued behind a running round doesn't 409.
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    if (!pendingMessage || thinking || busy) return;
    sendRef.current(pendingMessage);
    onPendingConsumed?.();
  }, [pendingMessage, thinking, busy, onPendingConsumed]);

  return (
    <div className="flex flex-col h-full min-w-0 bg-white dark:bg-gray-800">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Designer</h3>
        {chat && (
          <>
            <ContextUsageChip
              contextTokens={chat.context_tokens}
              contextWindow={chat.context_window}
            />
            {/* "New chat" keeps the design; the page header's "Reset design"
                wipes it. Both were called "Start over" at one point, a few
                pixels apart, doing very different amounts of work — which is
                how you press the wrong one and how you fail to find the right
                one. Each is now named for what it clears. */}
            <button
              type="button"
              onClick={newChat}
              disabled={thinking || busy || messages.length === 0}
              title="Begin a fresh conversation. The design is kept; earlier messages are no longer part of the conversation."
              className="px-2 py-0.5 text-[11px] rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              New chat
            </button>
            <AgentStatusChip status={chat.status} className="ml-auto" />
          </>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !thinking && (
          <div className="text-sm text-gray-500 dark:text-gray-400 space-y-3">
            <p>
              Describe how you want this site to look and I’ll set it up - colors, fonts,
              layout and navigation. Point me at a site you already have and I’ll match it.
            </p>
            <button
              type="button"
              onClick={designItForMe}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Design it for me
            </button>
          </div>
        )}

        {messages.map((message) => {
          const isHuman = message.entity_type === 'HUMAN';
          if (!message.content.trim() && message.has_tool_call) {
            return (
              <div
                key={message.id}
                className="text-[11px] text-gray-400 dark:text-gray-500 italic"
              >
                …working
              </div>
            );
          }
          return (
            <div
              key={message.id}
              className={`rounded-lg px-3 py-2 text-sm ${
                isHuman
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100 ml-6'
                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 mr-6'
              }`}
            >
              <ChatMarkdown text={message.content} />
            </div>
          );
        })}

        {thinking && (
          <div className="text-xs text-gray-400 dark:text-gray-500">Thinking…</div>
        )}
        <AgentErrorBanner error={chat?.last_error} className="border-b-0" />
        <AgentErrorBanner error={error} className="border-b-0" />
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
        <QuickPromptBar
          agentPrompts={chat?.suggested_prompts}
          staticPool={starterPrompts(spec, isPublished)}
          page={promptPage}
          onCycle={() => setPromptPage((p) => p + 1)}
          onSend={(prompt) => {
            // A prompt ending in a space is an unfinished sentence ("Copy the
            // colours from ") — put it in the box for the user to complete
            // instead of sending a question with no object.
            if (prompt.endsWith(' ')) setInput(prompt);
            else send(prompt);
          }}
          disabled={thinking || busy}
        />
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Make it dark with an orange accent…"
            disabled={thinking}
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={thinking || busy || !input.trim()}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 self-end"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default SiteScaffoldChatPane;
